import React, { useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { useTranslation } from 'react-i18next';

import { appColors } from 'config/colors-config';
import { FinecoButton } from 'pages/assistant/components/FinecoButton';
import { FinecoPaperClipIcon } from 'shared/assets/svg/FinecoPaperClipIcon';
import { IconNote } from 'shared/components/ui/IconNote';
import { useToast } from 'shared/components/ui/use-toast';
import { createLogger } from 'shared/logger';
import type { ManualTableSeed } from 'shared/store/portfolioDocImportStore';
import { formatItalianNumber, formatItalianPercentageNo100 } from 'utils/formatUtils';
import {
  importPortfolioDocument,
  isAbortError,
  type ImportProgress,
  type ImportWarning,
  type PageOverview,
} from 'pages/assistant/services/portfolioDocumentImport';
import {
  canConfirm,
  computeTotals,
  defaultFigureMode,
  ERROR_MESSAGES,
  importErrorMessage,
  ISSUE_LABELS,
  MAX_PAGES,
  PORTFOLIO_DOC_ACCEPT,
  rowBlocker,
  sendableRows,
  summaryText,
  STATUS_LABELS,
  toImportRows,
  toManualRows,
  validateDocument,
  valuesModeAllowed,
  WARNING_LABELS,
  formatPages,
  weightsTotalOk,
  type FigureMode,
  type ImportRow,
} from './portfolioImportRows';

import styles from './portfolioImport.module.scss';

const log = createLogger('PortfolioDocumentImport');

// The upload + confirmation table of the composer paperclip dialog (the only
// upload entry point). It never submits: «Usa questi dati» hands the rows to onUse.

type Review = { rows: ImportRow[]; mode: FigureMode; printedTotal: number | null; warnings: ImportWarning[] };
type PagePick = { overview: PageOverview[]; selected: number[]; warnings: ImportWarning[] };

type Props = {
  onUse: (seed: ManualTableSeed) => void;
};

type FigureInputProps = {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  decimalScale?: number;
};

const FigureInput: React.FC<FigureInputProps> = ({ value, onChange, ariaLabel, disabled, decimalScale = 2 }) => (
  <NumericFormat
    value={value}
    valueIsNumericString
    thousandSeparator="."
    decimalSeparator=","
    allowedDecimalSeparators={[',', '.']}
    decimalScale={decimalScale}
    allowNegative={false}
    inputMode="decimal"
    aria-label={ariaLabel}
    className={styles.figureInput}
    disabled={disabled}
    onValueChange={(values) => onChange(values.value)}
  />
);

const STATUS_CLASS = {
  ok: styles.badgeOk,
  check_figures: styles.badgeCheck,
  isin_repaired: styles.badgeRepaired,
  out_of_catalog: styles.badgeOut,
};

export const PortfolioDocumentImport: React.FC<Props> = ({ onUse }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [pagePick, setPagePick] = useState<PagePick | null>(null);
  const [review, setReview] = useState<Review | null>(null);

  const showError = (message: string) =>
    toast({ title: t('global.error'), description: message, variant: 'destructive' });

  const upload = async (document: File, pages?: string) => {
    const controller = new AbortController();
    abortRef.current = controller;
    setUploading(true);
    setProgress(null);
    try {
      const result = await importPortfolioDocument(document, pages, {
        onProgress: setProgress,
        signal: controller.signal,
      });
      if (result.status === 'needs_pages') {
        const overview = result.page_overview ?? [];
        setReview(null);
        setPagePick({
          overview,
          selected: overview
            .filter((page) => page.isin_count > 0)
            .map((page) => page.page)
            .slice(0, MAX_PAGES),
          warnings: result.warnings,
        });
        return;
      }
      const rows = toImportRows(result.holdings);
      setPagePick(null);
      if (rows.length === 0) {
        setReview(null);
        showError(ERROR_MESSAGES.no_isin_found);
        return;
      }
      setReview({
        rows,
        mode: defaultFigureMode(rows),
        printedTotal: result.totals?.printed_total ?? null,
        warnings: result.warnings,
      });
    } catch (error) {
      if (isAbortError(error)) return; // the advisor cancelled: nothing to report
      // Content-free: no file name, no figures.
      log.error('portfolio document import failed', { status: (error as { status?: number })?.status });
      showError(importErrorMessage(error));
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setUploading(false);
      setProgress(null);
    }
  };

  const cancelUpload = () => abortRef.current?.abort();

  const handleFileSelected: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const selected = event.target.files?.[0];
    // Cleared so picking the same file again re-fires onChange.
    event.target.value = '';
    if (!selected) return;
    const invalid = validateDocument(selected);
    if (invalid) {
      showError(ERROR_MESSAGES[invalid]);
      return;
    }
    // A new document replaces whatever the previous one showed.
    setFile(selected);
    setPagePick(null);
    setReview(null);
    void upload(selected);
  };

  const updateRows = (update: (rows: ImportRow[]) => ImportRow[]) =>
    setReview((current) => current && { ...current, rows: update(current.rows) });
  const updateRow = (rowId: string, patch: Partial<ImportRow>) =>
    updateRows((rows) => rows.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));

  const togglePage = (page: number) =>
    setPagePick(
      (current) =>
        current && {
          ...current,
          selected: current.selected.includes(page)
            ? current.selected.filter((p) => p !== page)
            : [...current.selected, page],
        },
    );

  return (
    <div className={styles.importPanel}>
      <p className={styles.panelHint}>
        Carica il documento del portafoglio (PDF, JPEG, PNG, XLSX o CSV, max 20 MB). Controlla ISIN e importi nella
        tabella, poi usa i dati per l’analisi.
      </p>

      <div className={styles.pickerRow}>
        {/* Reading chosen pages shows its progress in the picker footer, not here. */}
        {uploading && !pagePick ? (
          <UploadProgress progress={progress} onCancel={cancelUpload} />
        ) : (
          <>
            <button
              type="button"
              className={styles.pickerButton}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              data-testid="doc-upload-pick"
            >
              <FinecoPaperClipIcon fill="#00549F" />
              {file ? 'Scegli un altro documento' : 'Scegli un documento'}
            </button>
            {file && <span className={styles.fileName}>{file.name}</span>}
          </>
        )}
        <input
          type="file"
          ref={fileInputRef}
          accept={PORTFOLIO_DOC_ACCEPT}
          className={styles.hiddenInput}
          aria-label="Documento di portafoglio"
          data-testid="doc-upload-file-input"
          onChange={handleFileSelected}
        />
      </div>

      {pagePick && (
        <PagePicker
          pick={pagePick}
          reading={uploading ? { progress, onCancel: cancelUpload } : null}
          onToggle={togglePage}
          onSelect={(pages) => setPagePick((current) => current && { ...current, selected: pages })}
          onAnalyze={() => file && upload(file, formatPages(pagePick.selected))}
        />
      )}

      {review && (
        <ConfirmationTable
          review={review}
          disabled={uploading}
          onRowChange={updateRow}
          onRemove={(rowId) => updateRows((rows) => rows.filter((row) => row.rowId !== rowId))}
          onModeChange={(mode) => setReview((current) => current && { ...current, mode })}
          onUse={() => onUse({ rows: toManualRows(review.rows, review.mode), byWeights: review.mode === 'weights' })}
        />
      )}
    </div>
  );
};

// Long documents take minutes: after this many seconds the advisor is told so.
const LONG_WAIT_S = 20;
const LONG_WAIT_HINT = 'I documenti lunghi possono richiedere alcuni minuti.';

const progressShare = (progress: ImportProgress | null) =>
  progress?.done != null && progress.total ? Math.min(progress.done / progress.total, 1) : null;
const elapsedLabel = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

const UploadProgress: React.FC<{ progress: ImportProgress | null; onCancel: () => void }> = ({
  progress,
  onCancel,
}) => {
  const share = progressShare(progress);
  return (
    <div className={styles.progressBox} role="status" data-testid="doc-upload-uploading">
      <div className={styles.progressLine}>
        <span className={styles.spinner} aria-hidden="true" />
        <span data-testid="doc-upload-progress-text">{progress?.message ?? 'Lettura del documento…'}</span>
        <button type="button" className={styles.cancelButton} onClick={onCancel} data-testid="doc-upload-cancel">
          Annulla
        </button>
      </div>
      {share !== null && progress?.stage === 'figures' && (
        <div className={styles.progressTrack} aria-hidden="true">
          <div className={styles.progressFill} style={{ width: `${Math.round(share * 100)}%` }} />
        </div>
      )}
      {(progress?.elapsedS ?? 0) >= LONG_WAIT_S && <span className={styles.progressHint}>{LONG_WAIT_HINT}</span>}
    </div>
  );
};

// Notices use the Fineco note component (one note per code); codes this build does not know yet show no text.
const Warnings: React.FC<{ warnings: ImportWarning[] }> = ({ warnings }) => {
  const known = warnings.filter((code) => WARNING_LABELS[code]);
  return known.length > 0 ? (
    <div className={styles.notes} role="status">
      {known.map((code) => (
        <IconNote key={code} type="attention" title="Attenzione" body={WARNING_LABELS[code]} />
      ))}
    </div>
  ) : null;
};

type Reading = { progress: ImportProgress | null; onCancel: () => void };

type PagePickerProps = {
  pick: PagePick;
  reading: Reading | null; // the chosen pages are being read: picker locked, progress in the footer
  onToggle: (page: number) => void;
  onSelect: (pages: number[]) => void;
  onAnalyze: () => void;
};

// Accepted design (design/DESIGN_SPEC.md, «Importa da documento» page selection): pages with ISINs are large
// tiles (number + ISIN count), the others compact faded tiles that still toggle; colours from appColors.
const tileColors = {
  '--tile-blue': appColors.FinecoBluePrimary,
  '--tile-blue-light': appColors.BlueLight,
  '--tile-black': appColors.FinecoBlack,
  '--tile-gray': appColors.Gray1,
  '--tile-gray-faded': appColors.gray2,
  '--tile-gray-line': appColors.Gray3,
  '--tile-line': appColors.FinecoGrayLight1,
  '--tile-surface': appColors.FinecoGrayLight3,
  '--tile-white': appColors.FinecoWhite,
  '--tile-red': appColors.red,
} as React.CSSProperties;

const isinLabel = (count: number) => `${count} ISIN`;

const PagePicker: React.FC<PagePickerProps> = ({ pick, reading, onToggle, onSelect, onAnalyze }) => {
  const [onlyIsin, setOnlyIsin] = useState(false);
  const withIsin = pick.overview.filter((page) => page.isin_count > 0);
  const shown = onlyIsin ? withIsin : pick.overview;
  const count = pick.selected.length;
  const tooMany = count > MAX_PAGES;
  const pageCap = pick.warnings.includes('over_page_cap');
  return (
    <div className={styles.pagePicker} style={tileColors} data-testid="doc-upload-page-picker">
      {pageCap && (
        <IconNote
          type="note"
          title={`Documento di ${pick.overview.length} pagine`}
          body={
            withIsin.length > 0
              ? `Puoi analizzare fino a ${MAX_PAGES} pagine. Abbiamo selezionato quelle in cui compaiono codici ISIN: controlla e conferma.`
              : `Puoi analizzare fino a ${MAX_PAGES} pagine: seleziona quelle con le posizioni del portafoglio.`
          }
        />
      )}
      <Warnings warnings={pick.warnings.filter((code) => code !== 'over_page_cap')} />
      {/* Accepted design (DESIGN_SPEC.md, reading progress in the footer, variant A): while reading, the disabled
          fieldset locks the filter, select all/none and every tile at once, and fades them. */}
      <fieldset
        className={reading ? `${styles.pageLock} ${styles.pageLocked}` : styles.pageLock}
        disabled={!!reading}
        aria-busy={!!reading}
      >
        <div className={styles.pageToolbar}>
          {withIsin.length > 0 && withIsin.length < pick.overview.length ? (
            <div className={styles.pageFilter} role="tablist" aria-label="Pagine mostrate">
              <button type="button" role="tab" aria-selected={!onlyIsin} onClick={() => setOnlyIsin(false)}>
                Tutte ({pick.overview.length})
              </button>
              <button type="button" role="tab" aria-selected={onlyIsin} onClick={() => setOnlyIsin(true)}>
                Con ISIN ({withIsin.length})
              </button>
            </div>
          ) : (
            <span />
          )}
          <div className={styles.pageActions}>
            <button
              type="button"
              className={styles.pageAction}
              onClick={() => onSelect(shown.map((page) => page.page))}
              data-testid="doc-upload-select-all"
            >
              Seleziona tutte
            </button>
            <button
              type="button"
              className={styles.pageAction}
              onClick={() => onSelect([])}
              data-testid="doc-upload-select-none"
            >
              Deseleziona tutte
            </button>
          </div>
        </div>
        <ul className={styles.pageTiles}>
          {shown.map((page) => {
            const on = pick.selected.includes(page.page);
            const hasIsin = page.isin_count > 0;
            return (
              <li key={page.page}>
                <button
                  type="button"
                  className={[
                    styles.pageTile,
                    hasIsin ? styles.pageTileIsin : styles.pageTileCompact,
                    on && styles.pageTileOn,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-pressed={on}
                  aria-label={`Pagina ${page.page}, ${hasIsin ? isinLabel(page.isin_count) : 'nessun ISIN'}`}
                  data-testid={`doc-upload-page-${page.page}`}
                  onClick={() => onToggle(page.page)}
                >
                  <span className={styles.pageTileNumber}>{page.page}</span>
                  {hasIsin && <span className={styles.pageTileMeta}>{isinLabel(page.isin_count)}</span>}
                  {hasIsin && on && (
                    <span className={styles.pageTileCheck} aria-hidden="true">
                      ✓
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </fieldset>
      {reading ? (
        <ReadingFooter {...reading} />
      ) : (
        <div className={styles.pageFooter}>
          <span
            className={tooMany ? styles.pageCounterOver : styles.pageCounter}
            role="status"
            data-testid="doc-upload-page-counter"
          >
            {tooMany
              ? `${count} pagine selezionate: il massimo è ${MAX_PAGES}`
              : `${count} di ${MAX_PAGES} pagine selezionate`}
          </span>
          <FinecoButton
            click={onAnalyze}
            copy="Analizza le pagine selezionate"
            testId="doc-upload-analyze-pages"
            disabled={count === 0 || tooMany}
          />
        </div>
      )}
    </div>
  );
};

// The footer while the chosen pages are read: stage text (+ elapsed, + long-wait hint) instead of the counter,
// «Annulla» instead of the primary button, and the divider fills as the progress bar in `figures` (the only stage
// with a real share: `isin` reports done = total) and sweeps in the other stages.
const ReadingFooter: React.FC<Reading> = ({ progress, onCancel }) => {
  const share = progress?.stage === 'figures' ? progressShare(progress) : null;
  const elapsedS = progress?.elapsedS ?? 0;
  return (
    <div className={`${styles.pageFooter} ${styles.readingFooter}`} data-testid="doc-upload-reading-footer">
      <span
        className={share === null ? `${styles.readingBar} ${styles.readingBarIndeterminate}` : styles.readingBar}
        aria-hidden="true"
      >
        <span style={share === null ? undefined : { width: `${Math.round(share * 100)}%` }} />
      </span>
      <div className={styles.readingStatus} role="status">
        <span className={styles.readingLine}>
          <span className={styles.spinner} aria-hidden="true" />
          <span data-testid="doc-upload-progress-text">{progress?.message ?? 'Lettura del documento…'}</span>
          {elapsedS > 0 && <span className={styles.readingElapsed}>{elapsedLabel(elapsedS)}</span>}
        </span>
        {elapsedS >= LONG_WAIT_S && <span className={styles.readingHint}>{LONG_WAIT_HINT}</span>}
      </div>
      <FinecoButton variant="white" copy="Annulla" click={onCancel} testId="doc-upload-cancel" />
    </div>
  );
};

type ConfirmationTableProps = {
  review: Review;
  disabled: boolean;
  onRowChange: (rowId: string, patch: Partial<ImportRow>) => void;
  onRemove: (rowId: string) => void;
  onModeChange: (mode: FigureMode) => void;
  onUse: () => void;
};

const ConfirmationTable: React.FC<ConfirmationTableProps> = ({
  review,
  disabled,
  onRowChange,
  onRemove,
  onModeChange,
  onUse,
}) => {
  const { rows, mode, printedTotal } = review;
  const totals = computeTotals(rows);
  const sendable = sendableRows(rows);
  const excludedCount = rows.length - sendable.length;
  const valuesAllowed = valuesModeAllowed(rows);
  // Each printed value is rounded to the cent: allow half a cent per row before flagging the printed total.
  const printedMismatch =
    printedTotal !== null && Math.abs(totals.documentValueSum - printedTotal) > 0.005 * rows.length + 1e-9;

  return (
    <>
      <Warnings warnings={review.warnings} />

      <div className={styles.modeRow}>
        {valuesAllowed ? (
          <div className={styles.modeChoice} role="radiogroup" aria-label="Cifra da analizzare">
            <label className={styles.modeOption}>
              <input
                type="radio"
                data-testid="doc-upload-mode-weights"
                checked={mode === 'weights'}
                onChange={() => onModeChange('weights')}
              />
              Peso
            </label>
            <label className={styles.modeOption}>
              <input
                type="radio"
                data-testid="doc-upload-mode-values"
                checked={mode === 'values'}
                onChange={() => onModeChange('values')}
              />
              Controvalore
            </label>
          </div>
        ) : (
          <span className={styles.modeNote}>Valute diverse: l’analisi usa i pesi.</span>
        )}
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table} data-testid="doc-upload-table">
          <thead>
            <tr>
              <th>Stato</th>
              <th>ISIN</th>
              <th>Descrizione</th>
              <th>Valuta</th>
              <th className={styles.colFigure}>Quantità</th>
              <th className={styles.colFigure}>Controvalore</th>
              <th className={styles.colFigure}>Peso %</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const blocker = rowBlocker(row, mode);
              const readOnly = disabled || !row.inCatalog;
              return (
                <tr
                  key={row.rowId}
                  className={row.inCatalog ? undefined : styles.excludedRow}
                  data-testid="doc-upload-row"
                  data-row-id={row.rowId}
                  data-status={row.status}
                >
                  <td>
                    <span
                      className={`${styles.badge} ${STATUS_CLASS[row.status]}`}
                      data-testid="doc-upload-status-badge"
                      data-status={row.status}
                    >
                      {STATUS_LABELS[row.status]}
                    </span>
                    {/* Codes this build does not know yet show no text. */}
                    {row.issues
                      .filter((issue) => ISSUE_LABELS[issue])
                      .map((issue) => (
                        <div key={issue} className={styles.issue}>
                          {ISSUE_LABELS[issue]}
                        </div>
                      ))}
                    {blocker && <div className={styles.blocker}>{blocker}</div>}
                  </td>
                  <td>
                    <input
                      className={styles.isinInput}
                      value={row.isin}
                      aria-label={`ISIN riga ${row.rowId}`}
                      maxLength={12}
                      disabled={readOnly}
                      onChange={(e) => onRowChange(row.rowId, { isin: e.target.value.toUpperCase().trim() })}
                    />
                  </td>
                  <td>{row.description ?? '—'}</td>
                  <td>{row.currency ?? '—'}</td>
                  <td className={styles.colFigure}>
                    <FigureInput
                      value={row.quantity}
                      onChange={(quantity) => onRowChange(row.rowId, { quantity })}
                      ariaLabel={`Quantità ${row.isin}`}
                      disabled={readOnly}
                      decimalScale={4}
                    />
                  </td>
                  <td className={styles.colFigure}>
                    <FigureInput
                      value={row.value}
                      onChange={(value) => onRowChange(row.rowId, { value })}
                      ariaLabel={`Controvalore ${row.isin}`}
                      disabled={readOnly}
                    />
                  </td>
                  <td className={styles.colFigure}>
                    <FigureInput
                      value={row.weight}
                      onChange={(weight) => onRowChange(row.rowId, { weight })}
                      ariaLabel={`Peso % ${row.isin}`}
                      disabled={readOnly}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => onRemove(row.rowId)}
                      disabled={disabled}
                      aria-label={`Rimuovi ${row.isin}`}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={styles.totals}>
        <div>
          Controvalore totale: {formatItalianNumber(totals.documentValueSum)}
          {printedTotal !== null && (
            <span className={printedMismatch ? styles.mismatch : undefined}>
              {' '}
              (nel documento: {formatItalianNumber(printedTotal)})
            </span>
          )}
        </div>
        <div>
          {mode === 'weights'
            ? `Totale pesi da analizzare: ${formatItalianPercentageNo100(totals.sentWeightSum)}`
            : `Controvalore da analizzare: ${formatItalianNumber(totals.sentValueSum)}`}
        </div>
      </div>

      {mode === 'weights' && sendable.length > 0 && !weightsTotalOk(rows) && (
        <div className={styles.notes} role="status">
          <IconNote type="attention" title="Attenzione" body="I pesi da analizzare devono totalizzare 100%." />
        </div>
      )}

      <div className={styles.summary}>{summaryText(sendable.length, excludedCount)}</div>

      <div className={styles.footer}>
        <FinecoButton
          click={onUse}
          copy="Usa questi dati"
          testId="doc-upload-confirm"
          disabled={disabled || !canConfirm(rows, mode)}
        />
      </div>
    </>
  );
};
